/*
 * Take card by phone, its own file so the client page, the job page and the invoice's Collect payment all use it.
 */
import type { ClientDetail, PayStart } from "@steward/shared";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { loadStripe, type Stripe, type StripeElements } from "@stripe/stripe-js";
import { api } from "../api/client";
import { Modal, useToast } from "../components/overlays";
import { Banner, Button, Skeleton } from "../components/parts";
import { errText } from "../components/work";

/** Stripe's card box in the app's own colors, dark or light like the page. */
function phoneLook() {
  const t = document.documentElement.dataset.theme;
  const dark = t === "dark" || (t !== "light" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  const css = getComputedStyle(document.documentElement);
  const v = (name: string, or: string) => css.getPropertyValue(name).trim() || or;
  return {
    theme: dark ? ("night" as const) : ("stripe" as const),
    variables: {
      colorPrimary: v("--brand", "#1f6f5c"),
      colorBackground: v("--color-surface", dark ? "#1c1c1e" : "#ffffff"),
      colorText: v("--color-text", dark ? "#ffffff" : "#000000"),
      borderRadius: "10px",
      fontSizeBase: "16px",
      spacingUnit: "4px",
    },
  };
}

/**
 * Take card by phone (owner, Oct 31): the client reads their card out and the office types it into Stripe's own card box,
 * so the number never reaches the Steward. Saved like a card they saved, marked Taken by phone (api billing/card-by-phone.ts).
 */
export function PhoneCard({
  c,
  onClose,
  onSaved,
}: {
  c: Pick<ClientDetail, "id" | "name">;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const box = useRef<HTMLDivElement>(null);
  const [setup, setSetup] = useState<PayStart | null>(null);
  const [stripe, setStripe] = useState<{ s: Stripe; e: StripeElements } | null>(null);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    const live = { on: true };
    // Read through a function: the dialog may close while Stripe loads.
    const gone = () => !live.on;
    void (async () => {
      try {
        const made = await api<PayStart>("POST", `clients/${c.id}/card/start`, {});
        if (gone()) return;
        setSetup(made);
        if (!made.fake && made.publishableKey) {
          const st = await loadStripe(made.publishableKey);
          if (!st || gone()) return;
          setStripe({ s: st, e: st.elements({ clientSecret: made.clientSecret, appearance: phoneLook() }) });
        }
      } catch (e) {
        if (!gone()) setErr(errText(e));
      }
    })();
    return () => {
      live.on = false;
    };
  }, [c.id]);
  // Mounted after React has drawn the box: the first open used to race Stripe loading and leave the box empty.
  useEffect(() => {
    if (!stripe || !box.current) return;
    // Card only: the office cannot use the client's Apple Pay or Google Pay. The country is US.
    const pay = stripe.e.create("payment", {
      layout: "tabs",
      fields: { billingDetails: { address: { country: "never" } } },
      wallets: { applePay: "never", googlePay: "never", link: "never" },
    });
    pay.on("ready", () => setReady(true));
    pay.on("loaderror", () => setErr("The card box did not load. Close this and try again."));
    pay.mount(box.current);
    return () => pay.destroy();
  }, [stripe]);
  const save = async () => {
    if (!setup) return;
    setBusy(true);
    setErr(null);
    try {
      if (stripe) {
        const r = await stripe.s.confirmSetup({
          elements: stripe.e,
          confirmParams: {
            return_url: window.location.href,
            payment_method_data: { billing_details: { address: { country: "US" } } },
          },
          redirect: "if_required",
        });
        if (r.error) throw new Error(r.error.message ?? "The card was not saved.");
      }
      const r = await api<{ saved: boolean }>("POST", `clients/${c.id}/card/done`, { intentId: setup.intentId });
      if (!r.saved) throw new Error("The card was not saved. Try again.");
      await qc.invalidateQueries({ queryKey: ["client", c.id] });
      // The job page shows the card on file too.
      void qc.invalidateQueries({ queryKey: ["job"] });
      toast(`Card saved for ${c.name}. Nothing was charged.`);
      if (onSaved) onSaved();
      else onClose();
    } catch (e) {
      setErr(e instanceof Error && !(e instanceof TypeError) ? e.message : errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Take card by phone"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} disabled={!setup || (!!stripe && !ready)} onClick={() => void save()}>
            Save card
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted small">
          Type the card as {c.name} reads it to you. Nothing is charged now. The card is marked as taken by phone, which is not
          their signature.
        </p>
        {setup && !setup.fake && setup.publishableKey ? <div ref={box} className="public-card" /> : null}
        {setup && (setup.fake || !setup.publishableKey) ? (
          <Banner type="notice">Test mode here. Save uses the test card 4242.</Banner>
        ) : null}
        {!err && !(setup && (setup.fake || !setup.publishableKey)) && !ready ? <Skeleton rows={2} /> : null}
        {err ? <Banner type="error">{err}</Banner> : null}
      </div>
    </Modal>
  );
}
